import { ChangeDetectionStrategy, Component, Input, OnChanges, ViewChild } from '@angular/core';
import { FinancialOverviewTableColumns } from '@app/shared/constants';
import { FilterType, LocationStatusEnum } from '@app/shared/enums/common.enums';
import { Filter, SortState, TableColumn } from '@app/shared/models/common.model';
import { FinancialOverviewLocation } from '@app/shared/models/financial-overview.model';
import { exportToExcel } from '@app/shared/utilities/excel-export';
import { LOCATION_STATUS_META, statusOptions } from '@app/shared/utilities/status.meta';
import { FilterDrawerComponent } from '../filter-drawer/filter-drawer.component';

@Component({
  selector: 'app-financial-overview-table',
  templateUrl: './financial-overview-table.component.html',
  styleUrl: '../volume-table/volume-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FinancialOverviewTableComponent implements OnChanges {
  @Input() locations: FinancialOverviewLocation[] = [];
  @ViewChild(FilterDrawerComponent) private drawer?: FilterDrawerComponent;

  readonly initialSort: SortState = { key: 'totalRevenue', dir: 'desc' };
  readonly filters: Map<string, Filter> = new Map([
    ['provinceId', { type: FilterType.MultiSelect, options: [] }],
    ['statusId', { type: FilterType.MultiSelect, options: [] }],
  ]);
  columns: Map<string, TableColumn> = new Map(FinancialOverviewTableColumns);
  rows: FinancialOverviewLocation[] = [];
  footerRow: Record<string, any> | null = null;
  searchText = '';
  private selectedProvinces: string[] = [];
  private selectedStatuses: number[] = [];

  get activeFilterCount(): number {
    return this.selectedProvinces.length + this.selectedStatuses.length;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  ngOnChanges(): void {
    this.columns = new Map(FinancialOverviewTableColumns);
    this.updateFilterOptions();
    this.recompute();
  }

  onSearch(value: string): void {
    this.searchText = value ?? '';
    this.recompute();
  }

  clearSearch(): void {
    this.onSearch('');
  }

  toggleFilters(): void {
    this.drawer?.toggleFilters();
  }

  applyFilters(value: any): void {
    this.selectedProvinces = (value?.provinceId ?? []).map((option: any) => option.id);
    this.selectedStatuses = (value?.statusId ?? []).map((option: any) => option.id);
    this.drawer?.toggleFilters();
    this.recompute();
  }

  clearFilters(isOpen: boolean = true): void {
    this.selectedProvinces = [];
    this.selectedStatuses = [];
    if (!isOpen) this.drawer?.filtersForm?.reset();
    this.recompute();
  }

  statusClass(statusId: number): string {
    return LOCATION_STATUS_META[statusId].css;
  }

  statusLabel(statusId: number): string {
    return LOCATION_STATUS_META[statusId].key;
  }

  isActive(statusId: number): boolean {
    return statusId === LocationStatusEnum.Active;
  }

  export(): void {
    const data = this.rows.map((row) => ({
      'Partner shop': row.storeName,
      City: row.city,
      Province: row.provinceCode,
      'HAL revenue': row.halRevenue,
      'Drop-off revenue': row.dropOffRevenue,
      'Courier revenue': row.totalRevenue,
      'Payout pre-tax': row.payoutPreTaxAmount,
      'Payout tax': row.payoutTaxAmount,
      'Estimated payout': row.payoutPostTaxAmount,
    }));
    exportToExcel(data, 'financial-overview-by-location.xlsx', 'Financial overview');
  }

  private updateFilterOptions(): void {
    const provinces = Array.from(new Set(this.locations.map((location) => location.provinceCode).filter(Boolean))).sort();
    const statusIds = Array.from(new Set(this.locations.map((location) => location.statusId))).sort((a, b) => a - b);
    this.filters.get('provinceId')!.options = provinces.map((province) => ({ id: province, value: province }));
    this.filters.get('statusId')!.options = statusOptions(LOCATION_STATUS_META).filter((option) => statusIds.includes(option.id));
  }

  private recompute(): void {
    let rows = this.locations;
    if (this.selectedProvinces.length) rows = rows.filter((row) => this.selectedProvinces.includes(row.provinceCode));
    if (this.selectedStatuses.length) rows = rows.filter((row) => this.selectedStatuses.includes(row.statusId));
    const search = this.searchText.trim().toLowerCase();
    if (search) {
      rows = rows.filter((row) => `${row.storeName} ${row.city} ${row.provinceCode}`.toLowerCase().includes(search));
    }
    this.rows = rows;
    this.footerRow = this.buildFooter();
  }

  private buildFooter(): Record<string, any> | null {
    if (!this.rows.length) return null;
    return {
      count: this.rows.length,
      totalRevenue: this.columnTotal('totalRevenue'),
      payoutPreTaxAmount: this.columnTotal('payoutPreTaxAmount'),
      payoutTaxAmount: this.columnTotal('payoutTaxAmount'),
      payoutPostTaxAmount: this.columnTotal('payoutPostTaxAmount'),
    };
  }

  private columnTotal(key: 'totalRevenue' | 'payoutPreTaxAmount' | 'payoutTaxAmount' | 'payoutPostTaxAmount'): number {
    return this.rows.reduce((sum, row) => sum + row[key], 0);
  }
}
